import { Form, Formik, type FormikErrors, type FormikHelpers } from "formik";
import type { ReactNode } from "react";
import { toast } from "sonner";
import type * as Yup from "yup";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { applyApiErrors } from "@/lib/form-errors";

interface CrudDialogProps<V extends object> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Whether this is an edit (vs. add) — drives the title verb + button label. */
  editing: boolean;
  /** Singular noun, e.g. "Property" → "Add Property" / "Edit Property". */
  noun: string;
  initialValues: V;
  validationSchema?: Yup.AnySchema;
  /** Persist the values; throw to surface a field/toast error. */
  onSave: (values: V) => Promise<void>;
  onSaved: () => void;
  children: ReactNode;
  className?: string;
}

/**
 * Formik dialog shell shared by every worksheet add/edit modal. Handles submit,
 * success toast, close, and mapping server validation errors back onto fields
 * (`applyApiErrors`). Callers supply the fields and an `onSave`.
 */
export function CrudDialog<V extends object>({
  open,
  onOpenChange,
  editing,
  noun,
  initialValues,
  validationSchema,
  onSave,
  onSaved,
  children,
  className = "sm:max-w-lg",
}: CrudDialogProps<V>) {
  async function handleSubmit(values: V, bag: FormikHelpers<V>) {
    try {
      await onSave(values);
      toast.success(`${noun} ${editing ? "updated" : "added"}`);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(applyApiErrors(err, { setErrors }, { fallback: `Couldn't save ${noun.toLowerCase()}.` }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={className}>
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit" : "Add"} {noun}
          </DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              {children}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {editing ? "Save Changes" : `Add ${noun}`}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
